import React, { useEffect, useState } from 'react';
import { getProductPriceHistory, type PriceHistoryEntry } from '@/services/products.service';
import { Badge } from '@/components/ui/badge';
import { Loader2, History, TrendingUp, TrendingDown, Plus, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
} from '@/components/ui/sheet';

interface PriceHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  clinicId: string;
  productDescription: string;
}

export const PriceHistoryDrawer: React.FC<PriceHistoryDrawerProps> = ({
  open,
  onClose,
  productId,
  clinicId,
  productDescription,
}) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<PriceHistoryEntry[]>([]);

  useEffect(() => {
    if (open && productId && clinicId) {
      loadHistory();
    }
  }, [open, productId, clinicId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      // Filter for selling_price only in clinic view
      const data = await getProductPriceHistory(clinicId, productId, 'selling_price');
      setHistory(data);
    } catch (error: any) {
      console.error('Error loading price history:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load price history',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPriceChange = (entry: PriceHistoryEntry) => {
    if (entry.is_initial_setting) {
      return (
        <div className="flex items-center gap-2 text-brand-teal">
          <Plus className="h-4 w-4" />
          <span className="font-semibold">Initial: ${entry.new_value_dollars.toFixed(2)}</span>
        </div>
      );
    }

    const changeAmount = entry.change_amount_dollars!;
    const isIncrease = entry.is_price_increase;
    const colorClass = isIncrease ? 'text-emerald-600' : 'text-red-500';
    const Icon = isIncrease ? TrendingUp : TrendingDown;

    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-slate-700">
          <span className="font-medium">${entry.old_value_dollars!.toFixed(2)}</span>
          <span className="text-slate-400">→</span>
          <span className="font-semibold text-slate-900">${entry.new_value_dollars.toFixed(2)}</span>
        </div>
        <div className={`flex items-center gap-1.5 ${colorClass} font-semibold text-sm`}>
          <Icon className="h-4 w-4" />
          <span>{isIncrease ? '+' : ''}{changeAmount.toFixed(2)}</span>
          <span className="font-normal text-xs opacity-80">
            ({((changeAmount / entry.old_value_dollars!) * 100).toFixed(1)}%)
          </span>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full !max-w-[520px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-teal/10 flex items-center justify-center">
              <History className="h-5 w-5 text-brand-teal" />
            </div>
            <div>
              <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
                Selling Price History
              </SheetTitle>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1 max-w-[280px]">
                {productDescription}
              </p>
            </div>
          </div>
          <SheetClose asChild>
            <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-10 h-10 border-3 border-brand-teal border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-slate-500">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <History className="h-8 w-8" />
              </div>
              <p className="text-sm font-medium text-slate-600">No price changes yet</p>
              <p className="text-xs text-slate-400 mt-1">Price history will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((entry, idx) => (
                <div
                  key={entry.id}
                  className="relative rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors"
                >
                  {/* Timeline connector */}
                  {idx < history.length - 1 && (
                    <div className="absolute left-[22px] top-full h-3 w-0.5 bg-slate-200" />
                  )}

                  <div className="flex gap-3">
                    {/* Timeline dot */}
                    <div className={`flex-shrink-0 mt-0.5 h-3 w-3 rounded-full ring-4 ring-white ${
                      entry.is_initial_setting 
                        ? 'bg-brand-teal' 
                        : entry.is_price_increase 
                        ? 'bg-emerald-500' 
                        : 'bg-red-500'
                    }`} />

                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge 
                            variant="outline" 
                            className="bg-brand-teal/10 text-brand-teal border-brand-teal/20 text-[11px] font-medium px-2 py-0.5"
                          >
                            Selling Price
                          </Badge>
                          <Badge 
                            variant="outline"
                            className={`text-[11px] font-medium px-2 py-0.5 ${
                              entry.change_source === 'manual' 
                                ? 'bg-slate-100 text-slate-700 border-slate-200' 
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                          >
                            {entry.change_source === 'manual' ? 'Manual' : 'Bulk Excel'}
                          </Badge>
                        </div>
                        <span className="text-[11px] text-slate-400 whitespace-nowrap">
                          {formatDistanceToNow(new Date(entry.changed_at), { addSuffix: true })}
                        </span>
                      </div>

                      {/* Price change */}
                      <div className="mb-3">
                        {formatPriceChange(entry)}
                      </div>

                      {/* User info & timestamp */}
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="font-medium text-slate-700">{entry.changed_by_user_name}</span>
                          <span className="text-slate-300">•</span>
                          <span className="text-slate-400 truncate">{entry.changed_by_user_email}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1">
                          {format(new Date(entry.changed_at), 'MMM d, yyyy, h:mm:ss a')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </SheetContent>
    </Sheet>
  );
};

